import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  forwardRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { MatIcon } from '@angular/material/icon';
import { AvatarComponent } from '../avatar/avatar.component';
import { PersonOption } from '../person-select/person-select.component';

@Component({
  selector: 'app-person-multi-select',
  standalone: true,
  imports: [TranslatePipe, MatIcon, AvatarComponent, CdkOverlayOrigin, CdkConnectedOverlay],
  templateUrl: './person-multi-select.component.html',
  styleUrl: './person-multi-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PersonMultiSelectComponent),
      multi: true,
    },
  ],
})
export class PersonMultiSelectComponent implements ControlValueAccessor {
  readonly label = input<string>('');
  readonly placeholderKey = input<string>('personSelect.multiPlaceholder');
  readonly searchPlaceholderKey = input<string>('personSelect.searchPlaceholder');
  readonly emptyKey = input<string>('personSelect.empty');
  readonly removeKey = input<string>('personSelect.remove');
  readonly options = input<PersonOption[]>([]);

  readonly open = signal(false);
  readonly query = signal('');
  readonly disabled = signal(false);
  readonly activeIndex = signal(-1);

  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('triggerBtn');
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private readonly internalValue = signal<string[]>([]);
  readonly value = this.internalValue.asReadonly();

  readonly activeId = computed<string | null>(() => {
    const idx = this.activeIndex();
    const items = this.filtered();
    if (idx < 0 || idx >= items.length) return null;
    return this.optionDomId(items[idx].id);
  });

  readonly selected = computed<PersonOption[]>(() => {
    const ids = this.internalValue();
    const byId = new Map(this.options().map((o) => [o.id, o]));
    return ids.map((id) => byId.get(id) ?? { id, displayName: id });
  });

  readonly filtered = computed(() => {
    const q = this.query().trim().toLowerCase();
    const opts = this.options();
    if (!q) return opts;
    return opts.filter((o) => o.displayName.toLowerCase().includes(q));
  });

  private onChange: (value: string[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: string[] | null | undefined): void {
    this.internalValue.set(value ? [...value] : []);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    if (isDisabled) this.open.set(false);
  }

  isSelected(id: string): boolean {
    return this.internalValue().includes(id);
  }

  optionDomId(id: string): string {
    return `pms-opt-${id}`;
  }

  isActive(id: string): boolean {
    return this.activeId() === this.optionDomId(id);
  }

  toggle(): void {
    if (this.disabled()) return;
    if (this.open()) {
      this.close();
      return;
    }
    this.query.set('');
    this.activeIndex.set(this.filtered().length > 0 ? 0 : -1);
    this.open.set(true);
    queueMicrotask(() => this.searchInput()?.nativeElement.focus());
  }

  close(returnFocus = true): void {
    if (!this.open()) return;
    this.open.set(false);
    this.activeIndex.set(-1);
    this.onTouched();
    if (returnFocus) {
      queueMicrotask(() => this.trigger()?.nativeElement.focus());
    }
  }

  toggleOption(id: string): void {
    const current = this.internalValue();
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    this.internalValue.set(next);
    this.onChange(next);
    this.onTouched();
  }

  remove(id: string): void {
    if (this.disabled()) return;
    const next = this.internalValue().filter((x) => x !== id);
    this.internalValue.set(next);
    this.onChange(next);
    this.onTouched();
  }

  onSearchInput(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
    this.activeIndex.set(this.filtered().length > 0 ? 0 : -1);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    const items = this.filtered();
    switch (event.key) {
      case 'ArrowDown':
        if (items.length === 0) return;
        event.preventDefault();
        this.activeIndex.update((i) => (i + 1) % items.length);
        return;
      case 'ArrowUp':
        if (items.length === 0) return;
        event.preventDefault();
        this.activeIndex.update((i) => (i - 1 + items.length) % items.length);
        return;
      case 'Home':
        if (items.length === 0) return;
        event.preventDefault();
        this.activeIndex.set(0);
        return;
      case 'End':
        if (items.length === 0) return;
        event.preventDefault();
        this.activeIndex.set(items.length - 1);
        return;
      case 'Enter': {
        event.preventDefault();
        const idx = this.activeIndex();
        if (idx >= 0 && idx < items.length) this.toggleOption(items[idx].id);
        return;
      }
      case 'Escape':
        event.preventDefault();
        this.close();
        return;
    }
  }
}
